import axiosInstance from "./Axios";

export class BrandApi {
  static getBrandList = async ({
    page,
    search,
    brand_country,
    status,
    startDate,
    endDate,
  }: {
    page: number;
    search: string;
    brand_country: string;
    status: string;
    startDate: string;
    endDate: string;
  }) => {
    try {
      const res = await axiosInstance.get(`admin/brand`, {
        params: {
          page,
          limit: 30,
          created_at: "desc",
          search,
          brand_country,
          status,
          startDate,
          endDate,
        },
      });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong !",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static getBrandById = async (brandId: string) => {
    try {
      const res = await axiosInstance.get(`/admin/brand/${brandId}`);

      return { data: res.data };
    } catch (err: any) {
      return {
        error: "Failed to fetch brand data",
        data: err?.response?.data,
      };
    }
  };

  static UploadSystemFonts = async (formData: FormData) => {
    try {
      const res = await axiosInstance.post("/system-fonts/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return {
        success: true,
        data: res.data.data,
        message: res.data.message,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.response?.data?.message || "System font upload failed",
        data: err?.response?.data,
      };
    }
  };

  static ListSystemFonts = async (params?: {
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const res = await axiosInstance.get(`system-fonts/list`, { params });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching fonts!",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static GetSystemFontVariantsByIds = async (ids: string) => {
    try {
      const res = await axiosInstance.get(`system-fonts/variants/get-by-ids`, {
        params: { ids },
      });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching font variants by IDs!",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static ListSystemFontVariants = async (params?: {
    search?: string;
    page?: number;
    limit?: number;
    system_font_id?: string;
  }) => {
    try {
      const res = await axiosInstance.get(`system-fonts/variants/list`, {
        params,
      });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching font variants!",
        data: err?.response?.data,
        success: false,
      };
    }
  };

  static DeleteSystemFont = async (fontId: string) => {
    try {
      const res = await axiosInstance.delete(`system-fonts/${fontId}`);

      return {
        success: true,
        data: res.data,
        message: res.data.message,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.response?.data?.message || "Failed to delete system font",
      };
    }
  };

  static GetFonts = async (params: {
    fonts_for: "agency" | "brand";
    brand_id?: string;
    agency_id?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const { fonts_for, ...restParams } = params;

      const endpoint =
        fonts_for === "brand" ? "/brand-fonts/fonts" : "/agency-fonts/fonts";

      const res = await axiosInstance.get(endpoint, { params: restParams });

      return {
        data: res.data.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching fonts!",
        success: false,
      };
    }
  };

  static GetAgencySettings = async (agencyId?: string) => {
    try {
      const params: any = {};

      if (agencyId) {
        params.agency_id = agencyId;
      }

      const res = await axiosInstance.get(
        "/agency-settings/fetch-agency-settings",
        { params },
      );

      return {
        data: res.data.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching agency settings!",
        success: false,
      };
    }
  };

  static UploadFonts = async (
    formData: FormData,
    fontsFor: "agency" | "brand" = "agency",
  ) => {
    try {
      const endpoint =
        fontsFor === "brand" ? "/brand-fonts/upload" : "/agency-fonts/upload";

      const res = await axiosInstance.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return {
        success: true,
        data: res.data.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.response?.data?.message || "Font upload failed",
      };
    }
  };

  static RemoveFont = async (
    id: string,
    fontsFor: "agency" | "brand" = "agency",
  ) => {
    try {
      const endpoint =
        fontsFor === "brand"
          ? `brand-fonts/delete/${id}`
          : `agency-fonts/delete/${id}`;

      const res = await axiosInstance.delete(endpoint);

      return {
        success: true,
        data: res.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while deleting font!",
      };
    }
  };

  static uploadBrandLogo = async (brandId: string, data: FormData) => {
    try {
      const res = await axiosInstance.post(
        `/admin/brand/${brandId}/logo`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return {
        data: res.data,
      };
    } catch (err: any) {
      return {
        error: "Something went wrong!",
        data: err?.response?.data,
      };
    }
  };

  static createBrandByLogoOnly = async (data: FormData) => {
    try {
      const res = await axiosInstance.post("/admin/brand/logo", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return {
        success: true,
        data: res.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.response?.data?.message || "Brand logo upload failed",
        data: err?.response?.data,
      };
    }
  };

  static updateBrandLogo = async (brandId: string, data: FormData) => {
    try {
      const res = await axiosInstance.put(
        `/admin/brand/${brandId}/logo`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.response?.data?.message || "Brand logo update failed",
        data: err?.response?.data,
      };
    }
  };

  static updateBrandDetails = async (brandId: string, data: any) => {
    try {
      const res = await axiosInstance.put(`/admin/brand/${brandId}`, data);

      return {
        success: true,
        data: res.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while updating brand!",
        data: err?.response?.data,
      };
    }
  };

  static createBrand = async (data: any) => {
    try {
      const res = await axiosInstance.post(`/admin/brand`, data);

      return {
        success: true,
        data: res.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while creating brand!",
        data: err?.response?.data,
      };
    }
  };

  static getBrandSettings = async (brandId: string) => {
    try {
      const res = await axiosInstance.get(
        `/admin/brand/brand_settings/${brandId}`,
      );

      return {
        success: true,
        data: res.data?.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching brand settings!",
        data: err?.response?.data,
      };
    }
  };

  static CreateAgencySettings = async (payload: {
    agency_id?: string;
    font_configuration: string;
    title_fonts?: string;
    subtitle_fonts?: string;
    body_text_fonts?: string;
  }) => {
    try {
      const res = await axiosInstance.post("/agency-settings/create", payload);

      return {
        success: true,
        data: res.data.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while saving agency settings!",
      };
    }
  };

  static UpdateAgencySettings = async (payload: {
    agency_id?: string;
    font_configuration?: string;
    title_fonts?: string;
    subtitle_fonts?: string;
    body_text_fonts?: string;
  }) => {
    try {
      const res = await axiosInstance.put(
        "/agency-settings/update-agency-settings",
        payload,
      );

      return {
        success: true,
        data: res.data.data,
      };
    } catch (err: any) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Something went wrong while updating agency settings!",
      };
    }
  };

  static ListBrandFontVariants = async (params: {
    brand_id: string;
    search?: string;
    page?: number;
    limit?: number;
    brand_font_id?: string;
  }) => {
    try {
      const res = await axiosInstance.get(`brand-fonts/variants/list`, {
        params,
      });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching brand font variants!",
        success: false,
      };
    }
  };

  static GetBrandFontVariantsByIds = async (ids: string) => {
    try {
      const res = await axiosInstance.get(`brand-fonts/variants/get-by-ids`, {
        params: { ids },
      });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching brand font variants by IDs!",
        success: false,
      };
    }
  };

  static ListAgencyFontVariants = async (params: {
    agency_id: string;
    search?: string;
    page?: number;
    limit?: number;
    agency_font_id?: string;
  }) => {
    try {
      const res = await axiosInstance.get(`agency-fonts/variants/list`, {
        params,
      });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching agency font variants!",
        success: false,
      };
    }
  };

  static GetAgencyFontVariantsByIds = async (ids: string) => {
    try {
      const res = await axiosInstance.get(`agency-fonts/variants/get-by-ids`, {
        params: { ids },
      });

      return {
        data: res.data,
        success: true,
      };
    } catch (err: any) {
      return {
        error:
          err?.response?.data?.message ||
          "Something went wrong while fetching agency font variants by IDs!",
        success: false,
      };
    }
  };
}
